import React, { useEffect, useState } from "react";
import {
  Button, FormControl, FormHelperText,
  InputLabel, MenuItem, Select, Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const methods = [
  { key: "pix", label: "Pix" },
  { key: "card", label: "Cartão de crédito" },
  { key: "boleto", label: "Boleto bancário" }
];

const names = {
  asaas: "Asaas",
  mercadopago: "Mercado Pago",
  stripe: "Stripe",
  efi_pix: "Efí"
};

function extractSelections(data) {
  const result = {};

  methods.forEach(({ key }) => {
    result[key] = data.methods[key].selectedProvider || "auto";
  });

  return result;
}

export default function VenturaBillingGatewaySettings() {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get("/admin/billing-gateways");

      if (
        !data ||
        !data.methods ||
        methods.some(({ key }) => !data.methods[key])
      ) {
        throw new Error("Resposta incompleta");
      }

      setConfig(data);
      setSelected(extractSelections(data));
    } catch (err) {
      setError(
        "Não foi possível carregar as integrações de pagamento."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(method) {
    if (saving) return;

    setSaving(method);

    try {
      const { data } = await api.put(
        "/admin/billing-gateways",
        { [method]: selected[method] || "auto" }
      );

      if (!data || !data.methods || !data.methods[method]) {
        throw new Error("Resposta incompleta");
      }

      setConfig(data);
      setSelected(extractSelections(data));
      toast.success("Gateway atualizado com sucesso.");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Não foi possível salvar o gateway."
      );
    } finally {
      setSaving("");
    }
  }

  return (
    <div
      data-ventura-billing-gateways="v1"
      style={{
        width: "100%",
        padding: 20,
        marginBottom: 24,
        border: "1px solid rgba(128,128,128,0.3)",
        borderRadius: 10
      }}
    >
      <Typography variant="h6" gutterBottom>
        Bancos responsáveis por cada forma de pagamento
      </Typography>

      <Typography variant="body2" style={{ marginBottom: 20 }}>
        Escolha qual gateway receberá os pagamentos das
        assinaturas Ventura Company. O cliente verá apenas
        Pix, cartão de crédito ou boleto.
      </Typography>

      {loading ? (
        <Typography>Carregando integrações...</Typography>
      ) : error ? (
        <div>
          <Typography color="error">{error}</Typography>
          <Button onClick={load} color="primary">
            Tentar novamente
          </Button>
        </div>
      ) : config ? (
        <>
          {methods.map(({ key, label }) => {
            const item = config.methods[key];
            const available = item.availableProviders || [];
            const choice = selected[key] || "auto";
            const stored = item.selectedProvider || "auto";
            const active = item.effectiveProvider;

            return (
              <div key={key} style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "center",
                marginBottom: 20
              }}>
                <FormControl variant="outlined" size="small"
                  style={{ flex: "1 1 240px", maxWidth: 460 }}>
                  <InputLabel id={"gateway-" + key}>
                    {label}
                  </InputLabel>

                  <Select
                    labelId={"gateway-" + key}
                    label={label}
                    value={choice}
                    disabled={
                      Boolean(saving) ||
                      (
                        available.length === 0 &&
                        !item.selectedProvider
                      )
                    }
                    onChange={e => setSelected(previous => ({
                      ...previous,
                      [key]: e.target.value
                    }))}
                  >
                    <MenuItem value="auto">
                      Automático (um gateway disponível)
                    </MenuItem>

                    {available.map(provider => (
                      <MenuItem key={provider} value={provider}>
                        {names[provider] || provider}
                      </MenuItem>
                    ))}

                    {item.selectedProvider &&
                     !available.includes(item.selectedProvider) && (
                      <MenuItem
                        value={item.selectedProvider}
                        disabled
                      >
                        {names[item.selectedProvider] ||
                          item.selectedProvider} (indisponível)
                      </MenuItem>
                    )}
                  </Select>

                  <FormHelperText>
                    {active
                      ? "Configurado: " +
                        (names[active] || active) +
                        (item.selectionMode === "automatic"
                          ? " (automático)"
                          : "")
                      : available.length === 0
                        ? "Nenhuma integração compatível cadastrada"
                        : item.selectionMode === "needs_selection"
                          ? "Selecione o gateway responsável"
                          : "Gateway indisponível"}
                  </FormHelperText>
                </FormControl>

                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  disabled={
                    Boolean(saving) ||
                    choice === stored ||
                    (
                      choice !== "auto" &&
                      !available.includes(choice)
                    )
                  }
                  onClick={() => save(key)}
                >
                  {saving === key
                    ? "Salvando..."
                    : "Salvar escolha"}
                </Button>
              </div>
            );
          })}

          <Typography variant="caption" display="block">
            A seleção de gateway não ativa os pagamentos
            com cupons. Modalidades de pagamento dependem
            de integração e homologação financeira.
          </Typography>
        </>
      ) : null}
    </div>
  );
}
